"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.removeUserInputGate = exports.interposeUserInputGate = exports.removeNodesAndIncidentEdges = exports.newUserInputNeededNode = void 0;
const uuid_1 = require("uuid");
const common_1 = require("@finley-technologies/common");
// A userInputNeeded node the BBPL runner refuses to execute; the pipeline
// stalls there until an external action writes its success instance.
const newUserInputNeededNode = ({ referenceArtifact, inputType, }) => {
    return {
        id: (0, uuid_1.v4)(),
        type: 'userInputNeeded',
        configuration: { inputType },
        loanProductSlug: null,
        creditFacilitySlug: null,
        artifactDocumentId: referenceArtifact.artifactDocumentId,
        externalFilename: null,
        artifactSlug: `userInputNeeded_${referenceArtifact.artifactSlug}`,
    };
};
exports.newUserInputNeededNode = newUserInputNeededNode;
// Drops the nodes with `ids` and every edge touching them.
const removeNodesAndIncidentEdges = (dbGraph, ids) => {
    const removed = new Set(ids);
    return {
        dataTapeDependencies: dbGraph.dataTapeDependencies.filter(({ inputArtifactId, outputArtifactId }) => !removed.has(inputArtifactId) && !removed.has(outputArtifactId)),
        dataTapes: dbGraph.dataTapes.filter(({ id }) => !removed.has(id)),
    };
};
exports.removeNodesAndIncidentEdges = removeNodesAndIncidentEdges;
const edgeToString = (edge) => `${edge.inputArtifactId} => ${edge.outputArtifactId}`;
// Keeps the edges not listed in `removed`, then appends `added`.
const replaceEdges = (dbGraph, removed, added) => {
    const removedKeys = removed.map(edgeToString);
    return [
        ...dbGraph.dataTapeDependencies.filter((edge) => !removedKeys.includes(edgeToString(edge))),
        ...added,
    ];
};
const outputEdgesOf = (dbGraph, id) => dbGraph.dataTapeDependencies.filter(({ inputArtifactId }) => inputArtifactId === id);
// Before:  anchor ---> whatever
// After:   anchor ---> userInputNeeded(inputType) ---> whatever
//
// Interposes a userInputNeeded gate after each anchor and moves the anchor's
// outputs (if any) behind it.
const interposeUserInputGate = ({ dbGraph, anchors, inputType, }) => {
    const gates = [];
    const removed = [];
    const added = [];
    anchors.forEach((anchor) => {
        const gate = (0, exports.newUserInputNeededNode)({
            referenceArtifact: anchor,
            inputType,
        });
        gates.push(gate);
        added.push({ inputArtifactId: anchor.id, outputArtifactId: gate.id });
        outputEdgesOf(dbGraph, anchor.id).forEach((outputEdge) => {
            added.push({
                inputArtifactId: gate.id,
                outputArtifactId: outputEdge.outputArtifactId,
            });
            removed.push(outputEdge);
        });
    });
    return {
        dataTapes: [...dbGraph.dataTapes, ...gates],
        dataTapeDependencies: replaceEdges(dbGraph, removed, added),
    };
};
exports.interposeUserInputGate = interposeUserInputGate;
// Inverse of interposeUserInputGate: drops each gate and reconnects its single
// input straight to its outputs, so the round-trip is exact whether the gate
// was a leaf or interposed.
const removeUserInputGate = ({ dbGraph, gates, inputType, }) => {
    const removed = [];
    const added = [];
    gates.forEach((gate) => {
        (0, common_1.finleyAssert)(gate.configuration.inputType === inputType, 'Invalid configuration for userInputNeededNode');
        const inputNodeIds = dbGraph.dataTapeDependencies
            .filter(({ outputArtifactId }) => outputArtifactId === gate.id)
            .map(({ inputArtifactId }) => inputArtifactId);
        (0, common_1.finleyAssert)(inputNodeIds.length === 1, `userInputNeededNode must have exactly one artifact as input but [found:${inputNodeIds.length}], [id:${gate.id}]`);
        const anchorId = inputNodeIds[0];
        removed.push({ inputArtifactId: anchorId, outputArtifactId: gate.id });
        outputEdgesOf(dbGraph, gate.id).forEach((outputEdge) => {
            removed.push(outputEdge);
            added.push({
                inputArtifactId: anchorId,
                outputArtifactId: outputEdge.outputArtifactId,
            });
        });
    });
    const gateIds = gates.map(({ id }) => id);
    return {
        dataTapes: dbGraph.dataTapes.filter(({ id }) => !gateIds.includes(id)),
        dataTapeDependencies: replaceEdges(dbGraph, removed, added),
    };
};
exports.removeUserInputGate = removeUserInputGate;
//# sourceMappingURL=helpers.js.map